/**
 * Pure network fetch functions for the unified search pipeline (VOY-477).
 *
 * Lifted verbatim from `src/pages/search-plus.tsx` (lines 63-202 of the
 * pre-VOY-477 file). Kept as plain async functions — no React state,
 * no hooks — so the hook layer (`useSearchPipeline`) can call them and
 * tests can exercise them without a mount.
 *
 * Hotel fetching does NOT live here — `searchHotelsDirectly` from
 * `@/lib/hotels-api-backend` is already the right abstraction; the hook
 * uses it directly with its own retry-state callback.
 */

import { apiFetch } from '@/lib/api'
import type { FlightResult, ItineraryResponse, StructuredItinerary } from '@/types/travel'

/** What the Duffel-style flight search endpoint accepts. */
export interface FlightSearchParams {
  tripType?: 'round-trip' | 'one-way' | 'multi-city'
  origin?: string
  destination?: string
  departureDate?: string
  returnDate?: string
  segments?: Array<{ origin: string; destination: string; departureDate: string }>
  passengers?: { adults?: number; children?: number }
  cabinClass?: string
  // VOY-461: "Match my taste" — when true (+ authed), the backend applies the
  // flight reranker and attaches match_score/match_reasons (parity w/ hotels).
  personalize?: boolean
}

export async function searchFlightsViaBackend(params: FlightSearchParams): Promise<FlightResult[]> {
  const res = await apiFetch('/api/flights/search', {
    method: 'POST',
    body: JSON.stringify(params),
  })
  if (!res.ok) {
    throw new Error(await res.text())
  }
  const data = await res.json()
  const passengers = params.passengers || { adults: 1, children: 0 }
  // The flight search payload returns either a top-level array or a `{ flights }` envelope.
  // Preserve the existing search-plus normalisation: each flight gets the
  // passengers structure attached so downstream price math can read it.
  const raw = Array.isArray(data) ? data : data?.flights ?? []
  return (raw as FlightResult[]).map((f) => ({ ...f, passengers })) as FlightResult[]
}

export interface ItineraryFetcherParams {
  destination: string
  startDate: string
  endDate?: string
  duration?: number
  preferences?: string[]
}

export async function generateItineraryViaBackend(
  params: ItineraryFetcherParams,
): Promise<unknown> {
  const res = await apiFetch('/api/itinerary/openai', {
    method: 'POST',
    body: JSON.stringify(params),
  })
  if (!res.ok) {
    throw new Error(await res.text())
  }
  const data = await res.json()
  // Backend wraps the itinerary in an `{ itinerary: ... }` envelope on
  // some responses. Unwrap so downstream `parseItineraryResponse` sees
  // the inner payload. Matches pre-VOY-477 search-plus.tsx behaviour.
  return data?.itinerary ?? data
}

/**
 * Backend may return either a parsed structured itinerary, an `{ html }`
 * fallback envelope, or a raw string. Normalise to the discriminated union
 * that downstream renderers (`CollapsibleItineraryPreview`) consume.
 *
 * Moved from search-plus.tsx (parseItineraryResponse at line ~203).
 */
export function parseItineraryResponse(response: unknown): ItineraryResponse {
  // Already wrapped envelope.
  if (response && typeof response === 'object') {
    const obj = response as Record<string, unknown>
    if ('type' in obj && 'data' in obj) {
      return obj as unknown as ItineraryResponse
    }
    // Structured itinerary at the top level.
    if ('timeline' in obj || 'summary' in obj) {
      return { type: 'structured', data: obj as unknown as StructuredItinerary }
    }
    if (typeof obj.html === 'string') {
      return { type: 'html', data: obj.html }
    }
  }
  // JSON-encoded structured payload as a string (the backend occasionally
  // returns the structured itinerary as a stringified JSON blob).
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response)
      if (parsed && typeof parsed === 'object' && 'timeline' in parsed) {
        return { type: 'structured', data: parsed as StructuredItinerary }
      }
    } catch {
      // Not JSON — fall through to HTML.
    }
    return { type: 'html', data: response }
  }
  return { type: 'html', data: String(response ?? '') }
}
